import {
    selectIsSuperAdmin,
    selectIsBaseOrg,
    selectPermissions,
} from "@/auth/authSlice";
import { PERMISSION_CREATE_GLOBAL_BID_TEMPLATES } from "@/auth/constants";
import {
    allColumnProfiles,
    columnProfilesBase,
    getColumnProfiles,
    getLeftStickingColumnCount,
    getOriginalBidColumns,
    rightStickingColumnProfiles,
    rightStickingColumnProfilesRunning,
    stickyRunningColumns,
    visibleRunningColumns,
    updateDynamicColumnsFromBid,
} from "@/sheet/bidColumns";
import {
    BID_STATUS_DRAFT,
    bidCategories,
    bidSections,
} from "@/sheet/constants";
import reduxStore from "@/store";
import { uuidv4 } from "@/utils";
import { AddButtonCell } from "./AddButtonCell";
import {
    ADD_BUTTON_CELL_HEIGHT,
    ADDON_ROW_ID_PREFIX,
    BID_STATUS_PENDING_EP,
    BID_STATUS_PENDING_FINANCE,
    LINE_GROUP_ROW_ID_PREFIX,
    LINE_ITEM_ROW_ID_PREFIX,
    ROW_CELL_HEIGHT,
    HOURS_PER_DAY,
    WORK_DAYS_PER_WEEK,
    BID_STATUS_VERSION,
    LINE_RUNNING_COLUMN_ID_PREFIX,
    BID_STATUS_APPROVED,
    ADD_BUTTON_ROW_ID_PREFIX,
    SPACER_ROW_ID_PREFIX,
    SPACER_ROW_HEIGHT,
    bidStatuses,
} from "./constants";
import { getServiceItemsByCategory } from "./serviceItemsStore";
import { getRow, headerRow } from "./sheetUtils";

export const store = {
    bid: {},
    lineItemsHash: {},
    lineGroupsHash: {},
    lineItemsRunningHash: {},
    lineGroupsRunningHash: {},
    addonsHash: {},

    lineGroupToRunningMap: {},
    runningToLineGroupMap: {},
    lastRequestTimestamp: null,
    localData: {
        expansions: {
            lineItems: {},
            lineItemsRunning: {},
        },
        openedDropdownCell: {
            rowId: null,
            columnId: null,
        },
        bidFocus: { field: null, value: null },
        financialTermFocus: { id: null, field: null, value: null },
        billFocus: { id: null, field: null, value: null },
    },
    showRunning: false,
};

const fieldsSelectedValueMapping = {
    is_internal: {
        true: "Internal",
        false: "External",
    },
    "line_item_running.is_internal": {
        true: "Internal",
        false: "External",
    },
};

const getSpacerRow = () => {
    const processedColumnProfiles = getProcessedColumnProfiles();

    return getRow(
        { id: `${SPACER_ROW_ID_PREFIX}-${uuidv4()}` },
        processedColumnProfiles.map(() => ({
            id: "",
            type: "text",
            text: "FILL",
        })),
        false,
        null,
        0,
        SPACER_ROW_HEIGHT,
    );
};

const getAddButtonRow = (
    type,
    parentGroup,
    parentGroupRowId,
    serviceItems,
    depth,
) => {
    const processedColumnProfiles = getProcessedColumnProfiles();
    const addButtonHasChildren = false;
    const addButtonColumnProfile = processedColumnProfiles.map(
        (profile, index) =>
            profile.id == "name" ||
            profile.id == `${LINE_RUNNING_COLUMN_ID_PREFIX}.name`
                ? {
                      id: "name",
                      type: "text",
                      nonEditable: true,
                      alwaysVisible: true,
                      renderer: () => (
                          <div
                              style={{
                                  paddingLeft: depth * 20 + 32,
                                  width: "100%",
                              }}
                          >
                              <AddButtonCell
                                  type={type}
                                  parent={parentGroup || store.bid}
                                  serviceItemOptions={serviceItems}
                                  onAddGroup={(groupId) => {
                                      getExpantionSet()[groupId] = true;
                                  }}
                              />
                          </div>
                      ),
                  }
                : { id: "", type: "text", text: "" },
    );
    return getRow(
        { id: `${ADD_BUTTON_ROW_ID_PREFIX}-${uuidv4()}`, depth: depth },
        addButtonColumnProfile,
        addButtonHasChildren,
        parentGroupRowId,
        depth,
        ADD_BUTTON_CELL_HEIGHT,
    );
};

export const preProcessColumnProfiles = (
    subscriptionId,
    canViewRestrictedData,
    canEditExchangeRates,
) => {
    updateDynamicColumnsFromBid(store.bid);
    allColumnProfiles.forEach((col) => {
        if (col.formatter) {
            col.format = col.formatter(store.bid.project?.currency);
        }
        if (col.restricted && canViewRestrictedData) {
            col.restricted = false;
        }
        if (col.id == "exchange_rate_buffer") {
            col.nonEditable = !canEditExchangeRates;
        }
        if (typeof col.visible == "object" && col.visible != null) {
            col.visible = col.visible.includes(subscriptionId);
        }
    });
    const columnsProfiles = allColumnProfiles;
    const sticky_columns =
        store.bid.sticky_columns.concat(stickyRunningColumns()) || [];
    const visible_columns =
        store.bid.visible_columns.concat(visibleRunningColumns()) || [];
    columnsProfiles.forEach((col) => {
        col.sticky = sticky_columns.includes(col.id);
        col.visible = visible_columns.includes(col.id);
    });
};

export const getProcessedColumnProfiles = () => {
    const columnProfilesSet = getColumnProfiles();
    const processed = columnProfilesSet
        .filter((col) => (col.visible || col.alwaysVisible) && !col.restricted)
        .toSorted((a, b) => Boolean(a.sticky) - Boolean(b.sticky));
    return processed;
};

export const bidToRows = () => {
    const rootGroups = store.showRunning
        ? [...store.bid.line_items_grouped_running]
        : [...store.bid.line_items_grouped];

    const processedColumnProfiles = getProcessedColumnProfiles();
    const rows = [];
    let stack = [
        ...rootGroups,
        { addButton: "section", depth: 0, parent_id: null },
    ].reverse();
    while (stack.length > 0) {
        const currentGroup = stack.pop();
        const currentGroupHasChildren = true;
        const groupRowId = `${LINE_GROUP_ROW_ID_PREFIX}-${currentGroup.id}`;
        const groupParentRowId =
            currentGroup.parent_id &&
            `${LINE_GROUP_ROW_ID_PREFIX}-${currentGroup.parent_id}`;
        const lineItemHasChildren = false;
        const addonHasChildren = false;
        const currentGroupDepth = currentGroup.depth || 0;
        const nextGroupDepth = currentGroupDepth + 1;

        if (!currentGroup.parent_id) {
            rows.push(getSpacerRow());
        }

        if (currentGroup.addButton) {
            const parentGroup = getLineGroup(
                currentGroup.parent_id,
                store.showRunning,
            );
            if (parentGroup) {
                parentGroup.addons.forEach((addon) => {
                    const addonParentId =
                        addon.line_group_id &&
                        `${LINE_GROUP_ROW_ID_PREFIX}-${addon.line_group_id}`;
                    const addonRowId = `${ADDON_ROW_ID_PREFIX}-${addon.id}`;
                    rows.push(
                        getRow(
                            { ...addon, depth: nextGroupDepth, id: addonRowId },
                            processedColumnProfiles,
                            addonHasChildren,
                            addonParentId,
                            currentGroupDepth,
                            ROW_CELL_HEIGHT,
                            null,
                        ),
                    );
                });
            }

            rows.push(
                getAddButtonRow(
                    currentGroup.addButton,
                    parentGroup,
                    groupParentRowId,
                    currentGroup.addButton == "section"
                        ? bidSections
                        : bidCategories,
                    currentGroupDepth,
                ),
            );
            continue;
        }

        let skipColumns = ["is_internal", "rate_type", "markup"];
        skipColumns = skipColumns.concat(
            skipColumns.map((col) => `${LINE_RUNNING_COLUMN_ID_PREFIX}.${col}`),
        );
        rows.push(
            getRow(
                { ...currentGroup, id: groupRowId },
                processedColumnProfiles.map((profile, index) => {
                    const leftStickingCount = getLeftStickingColumnCount();
                    const isLeftSticking = index <= leftStickingCount;
                    const totalColumns = store.showRunning
                        ? rightStickingColumnProfilesRunning
                        : rightStickingColumnProfiles;
                    const isTotal = totalColumns.find(
                        (col) => col.id == profile.id,
                    );
                    if (isLeftSticking || isTotal) {
                        return profile;
                    }
                    return { id: "", type: "text", text: "" };
                }),
                currentGroupHasChildren,
                groupParentRowId,
                currentGroupDepth,
                ROW_CELL_HEIGHT,
                getExpantionSet()[groupRowId],
                store.localData.openedDropdownCell,
                false,
                fieldsSelectedValueMapping,
                skipColumns,
            ),
        );

        currentGroup.line_items.forEach((line) => {
            const lineItemParentId =
                line.line_group_id &&
                `${LINE_GROUP_ROW_ID_PREFIX}-${line.line_group_id}`;
            const lineItemRowId = `${LINE_ITEM_ROW_ID_PREFIX}-${line.id}`;
            rows.push(
                getRow(
                    { ...line, depth: nextGroupDepth, id: lineItemRowId },
                    processedColumnProfiles,
                    lineItemHasChildren,
                    lineItemParentId,
                    nextGroupDepth,
                    ROW_CELL_HEIGHT,
                    null,
                    store.localData.openedDropdownCell,
                    false,
                    fieldsSelectedValueMapping,
                ),
            );
        });

        if (currentGroup.line_items.length > 0 || currentGroup.depth >= 1) {
            currentGroup.addons.forEach((addon) => {
                const addonParentId =
                    addon.line_group_id &&
                    `${LINE_GROUP_ROW_ID_PREFIX}-${addon.line_group_id}`;
                const addonRowId = `${ADDON_ROW_ID_PREFIX}-${addon.id}`;
                rows.push(
                    getRow(
                        { ...addon, depth: nextGroupDepth, id: addonRowId },
                        processedColumnProfiles,
                        addonHasChildren,
                        addonParentId,
                        nextGroupDepth,
                        ROW_CELL_HEIGHT,
                        null,
                    ),
                );
            });
            const serviceItemNames = getServiceItemsByCategory(
                currentGroup.name,
            ).map((item) => item.name);
            rows.push(
                getAddButtonRow(
                    "line",
                    currentGroup,
                    groupRowId,
                    serviceItemNames,
                    nextGroupDepth,
                ),
            );
        }

        let childrenWithDepthInfo = [
            ...currentGroup.children.map((group) => {
                return { ...group, depth: nextGroupDepth };
            }),
        ];
        if (!currentGroup.parent_id) {
            childrenWithDepthInfo = [
                ...childrenWithDepthInfo,
                {
                    addButton: "category",
                    depth: nextGroupDepth,
                    parent_id: currentGroup.id,
                },
            ].reverse();
        }
        stack = stack.concat(childrenWithDepthInfo);
    }
    const header = headerRow(processedColumnProfiles);
    const allRows = [header, ...rows];
    return allRows;
};

const storeHashes = (bid) => {
    bid.line_items.forEach((line) => {
        store.lineItemsHash[line.id] = line;
    });
    bid.line_groups.forEach((group) => {
        store.lineGroupsHash[group.id] = group;
    });
    bid.line_items_running.forEach((line) => {
        store.lineItemsRunningHash[line.id] = line;
    });
    bid.line_groups_running.forEach((group) => {
        store.lineGroupsRunningHash[group.id] = group;
    });
    bid.addons.forEach((addon) => {
        store.addonsHash[addon.id] = addon;
    });
};

const constructLineItemsGrouped = (lineItems, lineGroups, addons) => {
    const sortedLineItems = lineItems
        .sort((a, b) => a.sort_order - b.sort_order)
        .filter((line) => !line.archived_at);
    const sections = lineGroups
        .filter((group) => !group.parent_id)
        .sort((a, b) => a.sort_order - b.sort_order)
        .filter((group) => !group.archived_at);
    const categories = lineGroups
        .filter((group) => group.parent_id)
        .sort((a, b) => a.sort_order - b.sort_order)
        .filter((group) => !group.archived_at);

    const sectionCategories = Object.fromEntries(
        sections.map((section) => [section.id, []]),
    );
    const categoryLineItems = Object.fromEntries(
        categories.map((category) => [category.id, []]),
    );
    const groupAddons = Object.fromEntries(
        lineGroups.map((group) => [group.id, []]),
    );

    categories.forEach((category) => {
        sectionCategories[category.parent_id].push(category);
        category.children = [];
        category.line_items = categoryLineItems[category.id].sort(
            (a, b) => a.sort_order - b.sort_order,
        );
        category.addons = groupAddons[category.id];
    });

    sections.forEach((section) => {
        section.line_items = [];
        section.children = sectionCategories[section.id].sort(
            (a, b) => a.sort_order - b.sort_order,
        );
        section.addons = groupAddons[section.id];
    });

    sortedLineItems.forEach((lineItem) => {
        categoryLineItems[lineItem.line_group_id].push(lineItem);
    });

    addons.forEach((addon) => {
        if (addon.line_group_id) {
            groupAddons[addon.line_group_id].push(addon);
        }
    });

    return sections;
};

export const storeRunningIdMap = (bid) => {
    const lineGroupToRunningMap = {};
    const runningToLineGroupMap = {};

    bid.line_groups_running.forEach((lineGroupRunning) => {
        lineGroupToRunningMap[
            `${LINE_GROUP_ROW_ID_PREFIX}-${lineGroupRunning.line_group_id}`
        ] = `${LINE_GROUP_ROW_ID_PREFIX}-${lineGroupRunning.id}`;
        runningToLineGroupMap[
            `${LINE_GROUP_ROW_ID_PREFIX}-${lineGroupRunning.id}`
        ] = `${LINE_GROUP_ROW_ID_PREFIX}-${lineGroupRunning.line_group_id}`;
    });

    store.lineGroupToRunningMap = { ...lineGroupToRunningMap };
    store.runningToLineGroupMap = { ...runningToLineGroupMap };
};

export const storeBid = (bid) => {
    store.bid = bid;
    store.bid.line_items_grouped = constructLineItemsGrouped(
        bid.line_items,
        bid.line_groups,
        bid.addons,
    );
    store.bid.line_items_grouped_running = constructLineItemsGrouped(
        bid.line_items_running,
        bid.line_groups_running,
        [],
    );
    store.bid.top_sheet_sender_ids = bid.top_sheet_senders.map((sender) => {
        return sender.id;
    });
    storeHashes(bid);
    storeBidComments(bid.comments);
    storeBidBills(bid.bills);
    storeInsights(
        bid,
        bid.status == BID_STATUS_DRAFT ||
            bid.status == BID_STATUS_PENDING_EP ||
            bid.status == BID_STATUS_PENDING_FINANCE,
    );
    storeRunningIdMap(bid);
};

export const storeBidComments = (allComments) => {
    // NOTE: can assume comments are only 2 levels deep
    const comments = allComments.filter((c) => c.archived_at == null);
    const rootComments = comments.filter((comment) => !comment.parent_id);
    const rootCommentChildren = Object.fromEntries(
        rootComments.map((comment) => [comment.id, []]),
    );
    comments.forEach((comment) => {
        if (comment.parent_id && rootCommentChildren[comment.parent_id]) {
            rootCommentChildren[comment.parent_id].push(comment);
        }
    });
    rootComments.forEach((comment) => {
        comment.children = rootCommentChildren[comment.id];
    });
    store.bid.root_comments = rootComments;

    const commentKeyMap = {};
    comments.forEach((comment) => {
        const commentKey = comment.meta?.comment_key;
        if (commentKey) {
            commentKeyMap[commentKey] = comment;
        }
    });

    const commentIdMap = {};
    comments.forEach((comment) => {
        commentIdMap[comment.id] = comment;
    });

    store.bid.comments_by_key = commentKeyMap;
    store.bid.comments_by_id = commentIdMap;
};

export const storeBidBills = (bills) => {
    store.bid.bills = bills.filter((bill) => bill.archived_at == null);
};

export const getLineItemsHash = (running = false) => {
    const line_items = running
        ? store.bid.line_items_running
        : store.bid.line_items;
    if (!line_items) {
        return {};
    }
    return Object.fromEntries(line_items.map((line) => [line.id, line]));
};

export const getLineGroupsHash = (running = false) => {
    const line_groups = running
        ? store.bid.line_groups_running
        : store.bid.line_groups;
    if (!line_groups) {
        return {};
    }
    return Object.fromEntries(line_groups.map((group) => [group.id, group]));
};

export const getAddonsHash = () => {
    if (!store.bid.addons) {
        return {};
    }
    return Object.fromEntries(
        store.bid.addons.map((addon) => [addon.id, addon]),
    );
};

export const getQuotedThreshold = (
    external_budget,
    external_percentage,
    internal_budget,
    internal_percentage,
) => {
    return (
        external_budget / (1 - external_percentage) +
        internal_budget / (1 - internal_percentage)
    );
};

export const storeInsights = (insights, includeBidinProject) => {
    const {
        external_quoted,
        internal_quoted,
        addons_quoted,
        total_quoted,
        external_budget,
        internal_budget,
        internal_budget_with_internal_rate,
        gross_profit,
        overhead,
        net_profit,
        external_budget_running,
        gross_profit_running,
        overhead_running,
        net_profit_running,
        project,
    } = insights;

    store.bid.insights = {
        external_quoted,
        internal_quoted,
        addons_quoted,
        total_quoted,
        external_budget,
        internal_budget,
        internal_budget_with_internal_rate,
        gross_profit,
        overhead,
        net_profit,
        external_budget_running,
        gross_profit_running,
        overhead_running,
        net_profit_running,
    };

    if (project) {
        store.bid.insights = {
            ...store.bid.insights,
            project_external_quoted: project.external_quoted,
            project_internal_quoted: project.internal_quoted,
            project_addons_quoted: project.addons_quoted,
            project_total_quoted: project.total_quoted,
            project_external_budget: project.external_budget,
            project_internal_budget: project.internal_budget,
            project_internal_budget_with_internal_rate:
                project.internal_budget_with_internal_rate,
            project_gross_profit: project.gross_profit,
            project_overhead: project.overhead,
            project_net_profit: project.net_profit,
            project_external_budget_running: project.external_budget_running,
            project_gross_profit_running: project.gross_profit_running,
            project_overhead_running: project.overhead_running,
            project_net_profit_running: project.net_profit_running,
        };
    }

    if (includeBidinProject) {
        store.bid.insights.project_external_quoted += external_quoted;
        store.bid.insights.project_internal_quoted += internal_quoted;
        store.bid.insights.project_addons_quoted += addons_quoted;
        store.bid.insights.project_total_quoted += total_quoted;
        store.bid.insights.project_external_budget += external_budget;
        store.bid.insights.project_internal_budget += internal_budget;
        store.bid.insights.project_internal_budget_with_internal_rate +=
            internal_budget_with_internal_rate;
        store.bid.insights.project_gross_profit += gross_profit;
        store.bid.insights.project_overhead += overhead;
        store.bid.insights.project_net_profit += net_profit;
        store.bid.insights.project_external_budget_running +=
            external_budget_running;
        store.bid.insights.project_gross_profit_running += gross_profit_running;
        store.bid.insights.project_overhead_running += overhead_running;
        store.bid.insights.project_net_profit_running += net_profit_running;
    }

    store.bid.insights.target_quoted = getQuotedThreshold(
        store.bid.insights.project_external_budget,
        0.15,
        store.bid.insights.project_internal_budget_with_internal_rate,
        0.5,
    );

    store.bid.insights.minimum_quoted = getQuotedThreshold(
        store.bid.insights.project_external_budget,
        0.1,
        store.bid.insights.project_internal_budget_with_internal_rate,
        0.5,
    );
};

export const getLineItem = (lineId, running = false) => {
    return getLineItemsHash(running)[lineId];
};

export const getLineGroup = (lineGroupId, running = false) => {
    return getLineGroupsHash(running)[lineGroupId];
};

export const getAddon = (addonId) => {
    return getAddonsHash()[addonId];
};

export const getOfficeCurrency = () => {
    return store.bid.project?.legal_entity.country.currency || "CAD";
};

export const getClientCurrency = () => {
    return store.bid.project?.currency || "CAD";
};

export const getReadOnly = () => {
    const isBaseOrg = selectIsBaseOrg(reduxStore.getState());
    const canCreateGlobalTemplates = selectPermissions(
        reduxStore.getState(),
    ).includes(PERMISSION_CREATE_GLOBAL_BID_TEMPLATES);

    return (
        store.bid.status != BID_STATUS_DRAFT ||
        store.bid.version_mode ||
        (store.bid.bid_template &&
            ((!store.bid.bid_template.person_id && !canCreateGlobalTemplates) ||
                (store.bid.bid_template.public && !isBaseOrg)))
    );
};

export const getIsTemplate = () => {
    return store.bid.bid_template != null;
};

export const getRootAddons = () => {
    if (!store.bid.addons) {
        return [];
    }
    return store.bid.addons.filter((addon) => !addon.line_group_id);
};

export const fitColumnWidthsToContainer = (container) => {
    const processedColumnProfiles = getProcessedColumnProfiles();
    const columnsToFit = processedColumnProfiles.filter(
        (profile) => !profile.defaultWidth,
    );
    const allVisibleFixedColumn = processedColumnProfiles.filter(
        (profile) => profile.defaultWidth,
    );
    const totalFixedWidth = allVisibleFixedColumn.reduce(
        (acc, col) => acc + col.width,
        0,
    );
    const newWidth =
        (container.clientWidth - totalFixedWidth) / columnsToFit.length;
    const width = Math.max(newWidth, 120);
    columnsToFit.forEach((profile) => {
        profile.width = width;
    });
};

const getSectionCellStyle = (first, last, expanded, theme) => {
    const style = {
        background: theme.palette.background.paper,
        border: {
            top: { color: theme.palette.background.paper },
            bottom: { color: theme.palette.background.paper },
            left: { color: theme.palette.background.paper },
            right: { color: theme.palette.background.paper },
        },
        fontWeight: 600,
    };

    if (expanded) {
        style.border.bottom.color = theme.palette.text.disabled;
        style.border.top.color = theme.palette.text.disabled;
        style.border.top.style = "solid";
        style.border.top.width = "1px";
    }

    if (first) {
        style.borderTopLeftRadius = "8px";
        if (expanded) {
            style.border.left.color = theme.palette.text.disabled;
        } else {
            style.borderBottomLeftRadius = "8px";
        }
    } else if (last) {
        style.borderTopRightRadius = "8px";
        if (expanded) {
            style.border.right.color = theme.palette.text.disabled;
        } else {
            style.borderBottomRightRadius = "8px";
        }
    }

    return style;
};

export const getStyledRows = (rowsData, theme) => {
    const readOnly = getReadOnly();
    const rows = [...rowsData];
    const processedColumnProfiles = getProcessedColumnProfiles();
    const stickyStyles =
        theme.palette.mode == "light"
            ? [
                  {
                      section: { color: "#225982", background: "#94D1FF" },
                      category: { color: "#225982", background: "#C8E7FE" },
                      line: { color: "#2C8FDA", background: "#EEF5FF" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F2" },
                  },
                  {
                      section: { color: "#724707", background: "#FFDAA2" },
                      category: { color: "#955D09", background: "#FCEACE" },
                      line: { color: "#CB7F0E", background: "#FAF7F2" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F2" },
                  },
                  {
                      section: { color: "#055832", background: "#95E7C1" },
                      category: { color: "#055832", background: "#BBEFD7" },
                      line: { color: "#148451", background: "#E8FCF3" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F2" },
                  },
              ]
            : [
                  {
                      section: { color: "#D5EDFF", background: "#0082E4" },
                      category: { color: "#D5EDFF", background: "#015A9D" },
                      line: { color: "#D5EDFF", background: "#002E50" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F222" },
                  },
                  {
                      section: { color: "#FFECD0", background: "#D08D28" },
                      category: { color: "#FFECD0", background: "#9F6A1C" },
                      line: { color: "#FFECD0", background: "#533911" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F222" },
                  },
                  {
                      section: { color: "#DAEEE5", background: "#21A468" },
                      category: { color: "#DAEEE5", background: "#1A754C" },
                      line: { color: "#DAEEE5", background: "#0C4129" },
                      disabled: { color: "#9E9E9E", background: "#F2F2F222" },
                  },
              ];

    let currentSection = null;

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const stickyStylesStack = [...stickyStyles];
        const isFirstRow = i == 0;
        const isLastRow = i == rows.length - 1;
        const isSection =
            row.rowId.split("-")[0] == LINE_GROUP_ROW_ID_PREFIX &&
            row.parentId == null;
        const isCategory =
            row.rowId.split("-")[0] == LINE_GROUP_ROW_ID_PREFIX &&
            row.parentId != null;
        const isAddButton =
            row.rowId.split("-")[0] == ADD_BUTTON_ROW_ID_PREFIX && !isLastRow;
        const isSpacer = row.rowId.split("-")[0] == SPACER_ROW_ID_PREFIX;

        if (isSection) {
            currentSection = row.rowId;
        } else if (isSpacer) {
            currentSection = null;
        }

        const isSectionLastRow = isAddButton && row.parentId == currentSection;
        const isExpanded = currentSection && getExpantionSet()[currentSection];

        for (let j = row.cells.length - 1; j >= 0; j--) {
            const cell = row.cells[j];
            const isFirstCol = j == 0;
            const isLastCol = j == row.cells.length - 1;
            const isSticky = processedColumnProfiles[j].sticky;
            const isRunningCustomId =
                processedColumnProfiles[j].isRunning &&
                processedColumnProfiles[j].id.includes("custom_id");

            if (!cell.style) {
                cell.style = {};
            }

            if (isFirstRow) {
                cell.style = {
                    ...cell.style,
                    border: {
                        top: { color: "transparent" },
                        bottom: { color: theme.palette.background.paper },
                        left: { color: theme.palette.background.paper },
                        right: { color: theme.palette.background.paper },
                    },
                };
            } else if (isLastRow) {
                cell.style = {
                    ...cell.style,
                    border: {
                        top: { color: theme.palette.background.paper },
                        bottom: { color: "transparent" },
                        left: { color: "transparent" },
                        right: { color: "transparent" },
                    },
                };
            } else if (isAddButton) {
                cell.style = {
                    ...cell.style,
                    border: {
                        top: { color: theme.palette.background.paper },
                        bottom: { color: theme.palette.background.paper },
                        left: { color: "transparent" },
                        right: { color: "transparent" },
                    },
                };
            } else {
                cell.style = {
                    ...cell.style,
                    boxShadow: "none",
                    border: {
                        top: { color: theme.palette.background.paper },
                        bottom: { color: theme.palette.background.paper },
                        left: { color: theme.palette.background.paper },
                        right: { color: theme.palette.background.paper },
                    },
                };
            }

            if (isFirstCol) {
                cell.style = {
                    ...cell.style,
                    justifyContent: "center",
                    border: {
                        ...cell.style.border,
                        left: {
                            color: isExpanded
                                ? theme.palette.text.disabled
                                : "transparent",
                        },
                    },
                };
            } else if (isLastCol) {
                cell.style = {
                    ...cell.style,
                    border: {
                        ...cell.style.border,
                        right: {
                            color: isExpanded
                                ? theme.palette.text.disabled
                                : "transparent",
                        },
                    },
                };
            }

            if (isSection) {
                const sectionStyle = getSectionCellStyle(
                    isFirstCol,
                    isLastCol,
                    isExpanded,
                    theme,
                );
                cell.style = {
                    ...cell.style,
                    ...sectionStyle,
                };
            } else if (isSectionLastRow) {
                cell.style = {
                    ...cell.style,
                    border: {
                        ...cell.style.border,
                        bottom: { color: theme.palette.text.disabled },
                    },
                };

                if (isFirstCol) {
                    cell.style.borderBottomLeftRadius = "8px";
                } else if (isLastCol) {
                    cell.style.borderBottomRightRadius = "8px";
                }
            } else if (isSpacer) {
                cell.style = {
                    ...cell.style,
                    border: {
                        top: { color: theme.palette.background.default },
                        bottom: { color: theme.palette.background.default },
                        left: { color: theme.palette.background.default },
                        right: { color: theme.palette.background.default },
                    },
                };
            }

            const stickyStyle = stickyStylesStack.pop() || stickyStyles[0];
            if (isSticky && !isSpacer && !isLastRow) {
                const cellStyle =
                    readOnly && !cell.isRunning
                        ? stickyStyle.disabled
                        : isSection
                          ? stickyStyle.section
                          : isCategory
                            ? stickyStyle.category
                            : stickyStyle.line;
                cell.style = {
                    ...cell.style,
                    ...cellStyle,
                };
            } else {
                if (readOnly || isRunningCustomId) {
                    cell.nonEditable =
                        !cell.isRunning ||
                        cell.nonEditable ||
                        isRunningCustomId;
                }
                if (cell.nonEditable) {
                    cell.style.color = "#9E9E9E";
                }
            }

            if (
                cell.columnId == `${LINE_RUNNING_COLUMN_ID_PREFIX}.variance` &&
                cell.value < 0
            ) {
                cell.style.color = theme.palette.error.main;
            }
            if (
                readOnly &&
                (cell.columnId ==
                    `${LINE_RUNNING_COLUMN_ID_PREFIX}.client_budget_buffered_original` ||
                    cell.columnId ==
                        `${LINE_RUNNING_COLUMN_ID_PREFIX}.client_quoted_original`)
            ) {
                cell.style.color =
                    theme.palette.mode == "dark" ? "#FFFFFF66" : "#00000055";
            }
        }
    }

    return rows;
};

export function getLineItemRate(existingRate, lineItem, rateType) {
    if (!lineItem || !lineItem.vendor_rate) {
        return existingRate;
    }

    switch (rateType) {
        case "Hours":
            return (
                Math.round((lineItem.vendor_rate / HOURS_PER_DAY) * 100) / 100
            );
        case "Days":
        case "Percentage":
            return lineItem.vendor_rate;
        case "Weeks":
            return lineItem.vendor_rate * WORK_DAYS_PER_WEEK;
        case "Flat rate":
        default:
            return existingRate;
    }
}

export function getIsProjectPepsi() {
    if (!store.bid.project || !store.bid.project.client) {
        return false;
    }
    return store.bid.project.client.name.includes("Pepsi");
}

export function getBidStatusIndex(status) {
    return bidStatuses.indexOf(status);
}

export function getBidStatusPastOrOn(pastStatus) {
    const pastIndex = getBidStatusIndex(pastStatus);
    const currentStatusIndex = getBidStatusIndex(store.bid.status);
    return currentStatusIndex >= pastIndex;
}

export function getShowRunning() {
    return store.showRunning;
}

export function getExpantionSet() {
    if (store.showRunning) {
        return store.localData.expansions.lineItemsRunning;
    } else {
        return store.localData.expansions.lineItems;
    }
}

export function setExpansion(rowId, expanded) {
    if (store.showRunning) {
        store.localData.expansions.lineItemsRunning[rowId] = expanded;
        const lineItemId = store.runningToLineGroupMap[rowId];
        if (lineItemId) {
            store.localData.expansions.lineItems[lineItemId] = expanded;
        }
    } else {
        store.localData.expansions.lineItems[rowId] = expanded;
        const lineItemRunningId = store.lineGroupToRunningMap[rowId];
        if (lineItemRunningId) {
            store.localData.expansions.lineItemsRunning[lineItemRunningId] =
                expanded;
        }
    }
}

export function matchRunningExpansionsToLineItems() {
    Object.entries(store.localData.expansions.lineItems).forEach(
        ([lineRowId, expanded]) => {
            const runningLineRowId = store.lineGroupToRunningMap[lineRowId];
            if (runningLineRowId) {
                store.localData.expansions.lineItemsRunning[runningLineRowId] =
                    expanded;
            }
        },
    );
}

export function optimisticToggleCleanBid(clean) {
    store.bid.line_items.forEach((line) => {
        if (line.client_quoted == 0) {
            line.archived_at = clean ? new Date().toISOString() : null;
        }
    });
    store.bid.line_groups.forEach((group) => {
        if (group.client_quoted == 0) {
            group.archived_at = clean ? new Date().toISOString() : null;
        }
    });
    store.bid.addons.forEach((addon) => {
        if (addon.client_quoted == 0) {
            addon.archived_at = clean ? new Date().toISOString() : null;
        }
    });
}
